import { InboxOutlined, LeftOutlined, RightOutlined, UserOutlined } from '@ant-design/icons';
import { App, Button, Form, Input, InputNumber, Modal, Spin } from 'antd';
import bg_home from 'assets/images/bg_home.png';
import wcTabPrev from 'assets/images/wc2026/previous.png';
import wcTabNext from 'assets/images/wc2026/next.png';
import wcTabScoreIcon from 'assets/images/wc2026/tab_ts.png';
import wcTabResultIcon from 'assets/images/wc2026/tab_wc.png';
import HeaderMenu from 'components/Header/HeaderMenu';
import { SITE } from 'config/config';
import dayjs from 'dayjs';
import qs from 'query-string';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Countdown from 'react-countdown';
import {
  createPredictions,
  getAllSetting,
  getBestPlayerPredictions,
  getFP,
  getIP,
  getMatchList,
  getPlayers,
  getTeams,
  getTeamsChampions,
  getTeamsEliminatedFirst,
  getTeamsMostGoalsInOneMatch,
  // getTeamsRound16First,
  getTime,
  getTopScorerPredictions,
  voteWorldCupBestPlayer,
  voteWorldCupChampion,
  voteWorldCupEliminatedFirst,
  voteWorldCupMostGoalsInOneMatch,
  // voteWorldCupRound16First,
  voteWorldCupTopScorer
} from 'service/base';
import { ChampionPredictionTab, PlayerPredictionTab } from './components/WorldCupPredictionTabs';
import { twMerge } from 'tailwind-merge';
import { CONST_SITE, LocalStorageKey } from 'utils/common/const';
import { AUTH_CHANGED_EVENT, getStoredAccessToken, requestOpenLoginModal } from 'utils/authSession';
import Rule from './components/Rule';

function unwrapApiList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.result)) return res.result;
  return [];
}

function mapTeamFromApi(t) {
  const id = String(
    t?.id ?? t?.teamId ?? t?._id ?? t?.code ?? t?.isoCode ?? t?.countryCode ?? ''
  ).trim();
  const name = t?.name_vi ?? t?.name ?? t?.teamName ?? t?.title ?? t?.label ?? '';
  let flag = t?.flag ?? t?.logo ?? t?.imageUrl ?? t?.image ?? t?.avatar ?? t?.photo ?? '';
  if (!flag && id && /^[a-z]{2}$/i.test(id)) {
    flag = `https://flagcdn.com/w80/${id.toLowerCase()}.png`;
  }
  if (!id || !name) return null;
  return { id, name, flag: flag || undefined };
}

function mapPlayerFromApi(p) {
  const id = String(p?.id ?? p?._id ?? p?.playerId ?? '').trim();
  const name = p?.name ?? p?.playerName ?? p?.fullName ?? '';
  const photo = p?.avatar ?? p?.photo ?? p?.image ?? p?.imageUrl ?? '';
  const teamName = p?.teamName ?? p?.team_name ?? '';
  if (!id || !name) return null;
  return {
    id,
    name,
    photo: photo || undefined,
    teamName: teamName || undefined,
    teamId: p?.teamId ?? p?.team_id,
    countryCode: p?.countryCode ?? p?.country_code
  };
}

function mapTopScorerPredictionRow(r, index) {
  const id = String(r?.id ?? r?._id ?? r?.playerId ?? '').trim();
  const name = r?.name ?? r?.playerName ?? '';
  const photo = r?.avatar ?? r?.photo ?? r?.image ?? '';
  const countRaw = r?.count ?? r?.votes ?? r?.voteCount ?? 0;
  const votes =
    typeof countRaw === 'number' ? countRaw : Number(String(countRaw).replace(/\D/g, '')) || 0;
  if (!id || !name) return null;
  return {
    id,
    name,
    votes,
    photo: photo || undefined,
    percent: typeof r?.percent === 'number' ? r.percent : undefined,
    teamName: r?.teamName ?? r?.team_name
  };
}

function mapTeamsChampionsRow(r, index) {
  const code = String(r?.code ?? '').trim();
  const name = r?.name_vi || r?.name || r?.teamName || r?.title || r?.label || '';
  const votesRaw = r?.count ?? r?.votes ?? r?.voteCount ?? r?.score ?? 0;
  const votes =
    typeof votesRaw === 'number' ? votesRaw : Number(String(votesRaw).replace(/\D/g, '')) || 0;
  let flag = r?.flag ?? r?.logo ?? r?.imageUrl ?? r?.image ?? '';
  const idGuess = code || (r?.id ?? r?.teamId ?? r?.countryCode);
  const idStr = String(idGuess ?? '').trim();
  if (!flag && idStr && /^[a-z]{2}$/i.test(idStr)) {
    flag = `https://flagcdn.com/w80/${idStr.toLowerCase()}.png`;
  }
  if (!name) return null;
  const safeId = code || idStr || `champion-row-${index}`;
  return { id: safeId, name, votes, flag: flag || undefined, percent: r?.percent };
}

/** Thứ tự & copy theo Figma node 72-910 */
const wcPredictionTabs = [
  { key: 'champion', label: 'đội vô địch' },
  { key: 'top-scorer', label: 'vua phá lưới' },
  // { key: 'first-eliminated', label: 'đội đầu tiên bị loại' },
  { key: 'best-player', label: 'cầu thủ xuất sắc' },
  { key: 'most-goals-in-one-match', label: 'đội có nhiều bàn thắng nhất 1 trận' }
];

const leagues = [
  {
    id: 1,
    name: 'UEFA Champions League',
    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/f/f5/UEFA_Champions_League.svg/960px-UEFA_Champions_League.svg.png'
  },
  {
    id: 2,
    name: 'UEFA Europa League',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/1/1b/UEFA_Europa_League_logo_%282024_version%29.svg'
  },
  {
    id: 3,
    name: 'English Premier League',
    logo: 'https://loodibee.com/wp-content/uploads/Premier-League-Logo.png'
  },
  {
    id: 4,
    name: 'La Liga',
    logo: 'https://api-ddts-on68.attcloud.org/public/uploads/file-1776497700383-85707437.png'
  },
  {
    id: 5,
    name: 'Italian Serie A',
    logo: 'https://1000logos.net/wp-content/uploads/2019/01/Italian-Serie-A-Logo.png'
  },
  {
    id: 6,
    name: 'German Bundesliga',
    logo: 'https://api-ddts-on68.attcloud.org/public/uploads/file-1776497741004-506738821.png'
  },
  {
    id: 7,
    name: 'France Ligue 1',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/Logo_Ligue_1_McDonald%27s_2024.svg/250px-Logo_Ligue_1_McDonald%27s_2024.svg.png'
  },
  {
    id: 8,
    name: 'FIFA World Cup',
    logo: 'https://upload.wikimedia.org/wikipedia/en/1/17/2026_FIFA_World_Cup_emblem.svg'
  }
];

const HI88Page = () => {
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [listDataMatch, setListDataMatch] = useState([]);
  const [timeServer, setTimeServer] = useState(null);
  const [IP, setIP] = useState(null);
  const [FP, setFP] = useState(null);
  const [dataSettings, setDataSettings] = useState([]);

  const [selectedLeagues, setSelectedLeagues] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'today', 'tomorrow'
  const [showSchedulePopup, setShowSchedulePopup] = useState(false);

  /** Tab chính Figma: Dự đoán tỉ số | Dự đoán kết quả (World Cup 2026) */
  const [mainHomeTab, setMainHomeTab] = useState('score');
  const [activeWcTab, setActiveWcTab] = useState('champion');
  const wcTabsScrollRef = useRef(null);

  const [championQuery, setChampionQuery] = useState('');
  const [isChampionAutoFilled, setIsChampionAutoFilled] = useState(false);
  const [selectedChampionId, setSelectedChampionId] = useState(null);
  const [isChampionVoting, setIsChampionVoting] = useState(false);
  const [eliminatedFirstQuery, setEliminatedFirstQuery] = useState('');
  const [isEliminatedFirstAutoFilled, setIsEliminatedFirstAutoFilled] = useState(false);
  const [selectedEliminatedFirstId, setSelectedEliminatedFirstId] = useState(null);
  const [isEliminatedFirstVoting, setIsEliminatedFirstVoting] = useState(false);
  const [mostGoalsOneMatchQuery, setMostGoalsOneMatchQuery] = useState('');
  const [isMostGoalsOneMatchAutoFilled, setIsMostGoalsOneMatchAutoFilled] = useState(false);
  const [selectedMostGoalsOneMatchId, setSelectedMostGoalsOneMatchId] = useState(null);
  const [isMostGoalsOneMatchVoting, setIsMostGoalsOneMatchVoting] = useState(false);
  const [round16FirstQuery, setRound16FirstQuery] = useState('');
  const [isRound16FirstAutoFilled, setIsRound16FirstAutoFilled] = useState(false);
  const [selectedRound16FirstId, setSelectedRound16FirstId] = useState(null);
  const [isRound16FirstVoting, setIsRound16FirstVoting] = useState(false);
  const [topScorerQuery, setTopScorerQuery] = useState('');
  const [isTopScorerAutoFilled, setIsTopScorerAutoFilled] = useState(false);
  const [selectedTopScorerId, setSelectedTopScorerId] = useState(null);
  const [isTopScorerVoting, setIsTopScorerVoting] = useState(false);
  const [bestPlayerQuery, setBestPlayerQuery] = useState('');
  const [isBestPlayerAutoFilled, setIsBestPlayerAutoFilled] = useState(false);
  const [selectedBestPlayerId, setSelectedBestPlayerId] = useState(null);
  const [isBestPlayerVoting, setIsBestPlayerVoting] = useState(false);
  const [voteConfirm, setVoteConfirm] = useState({
    open: false,
    type: '',
    title: '',
    selectedName: '',
    payload: null,
    error: ''
  });

  const [championTeams, setChampionTeams] = useState([]);
  const [wcPlayers, setWcPlayers] = useState([]);
  const [championLeaderboard, setChampionLeaderboard] = useState([]);
  const [eliminatedFirstLeaderboard, setEliminatedFirstLeaderboard] = useState([]);
  const [mostGoalsOneMatchLeaderboard, setMostGoalsOneMatchLeaderboard] = useState([]);
  const [round16FirstLeaderboard, setRound16FirstLeaderboard] = useState([]);
  const [topScorerLeaderboard, setTopScorerLeaderboard] = useState([]);
  const [bestPlayerLeaderboard, setBestPlayerLeaderboard] = useState([]);

  const topScorerPlayersList = useMemo(() => {
    return wcPlayers.map((p) => ({
      id: String(p.id),
      name: p.name,
      teamName: p.teamName,
      photo: p.photo
    }));
  }, [wcPlayers]);

  const bestPlayerPlayersList = useMemo(() => {
    return wcPlayers.map((p) => ({
      id: String(p.id),
      name: p.name,
      teamName: p.teamName,
      photo: p.photo
    }));
  }, [wcPlayers]);

  // Hàm lọc trận đấu theo tab được chọn
  const getFilteredMatches = () => {
    if (!listDataMatch || listDataMatch.length === 0) return [];

    const now = dayjs();
    const today = now.format('YYYY-MM-DD');
    const tomorrow = now.add(1, 'day').format('YYYY-MM-DD');

    switch (activeTab) {
      case 'today':
        return listDataMatch.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === today;
        });
      case 'tomorrow':
        return listDataMatch.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === tomorrow;
        });
      case 'all':
      default:
        return listDataMatch;
    }
  };

  const handleLeagueClick = (leagueId) => {
    setSelectedLeagues((prevSelectedLeagues) =>
      prevSelectedLeagues.includes(leagueId)
        ? prevSelectedLeagues.filter((id) => id !== leagueId)
        : [...prevSelectedLeagues, leagueId]
    );
  };

  const [scrollPosition, setScrollPosition] = useState(0);
  const leagueListRef = React.createRef();

  const scrollLeft = () => {
    if (leagueListRef.current) {
      leagueListRef.current.scrollBy({ left: -100, behavior: 'smooth' });
      setScrollPosition(leagueListRef.current.scrollLeft - 100);
    }
  };

  const scrollRight = () => {
    if (leagueListRef.current) {
      leagueListRef.current.scrollBy({ left: 100, behavior: 'smooth' });
      setScrollPosition(leagueListRef.current.scrollLeft + 100);
    }
  };

  useEffect(() => {
    fetchAllSetting();
    fetchIP();
    fetchFP();
    fetchTimeServer();
  }, []);

  useEffect(() => {
    fetchDataScheduByLS();
  }, []);

  const syncPredictionAccountFromAuth = useCallback(() => {
    const token = localStorage.getItem(LocalStorageKey.ACCESS_TOKEN);
    if (!token) {
      form.setFieldsValue({ account: '' });
      return;
    }
    const username = localStorage.getItem('hi88_login_username') || 'Thành viên';
    form.setFieldsValue({ account: username });
  }, [form]);

  useEffect(() => {
    syncPredictionAccountFromAuth();
    const onAuth = () => syncPredictionAccountFromAuth();
    const onStorage = (e) => {
      if (e.key === LocalStorageKey.ACCESS_TOKEN || e.key === 'hi88_login_username') {
        syncPredictionAccountFromAuth();
      }
    };
    window.addEventListener(AUTH_CHANGED_EVENT, onAuth);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, onAuth);
      window.removeEventListener('storage', onStorage);
    };
  }, [syncPredictionAccountFromAuth]);

  useEffect(() => {
    let cancelled = false;
    const apiParams = { params: { site: CONST_SITE.HI88 } };
    (async () => {
      try {
        const [
          teamsRes,
          playersRes,
          championsRes,
          eliminatedFirstRes,
          mostGoalsOneMatchRes,
          topScorerPredRes,
          bestPlayerPredRes
        ] = await Promise.all([
          getTeams(apiParams),
          getPlayers(apiParams),
          getTeamsChampions(),
          getTeamsEliminatedFirst(),
          getTeamsMostGoalsInOneMatch(),
          getTopScorerPredictions(apiParams),
          getBestPlayerPredictions(apiParams)
        ]);
        if (cancelled) return;
        const teams = unwrapApiList(teamsRes).map(mapTeamFromApi).filter(Boolean);
        setChampionTeams(teams);
        const rawPlayers =
          playersRes?.code === 0 && Array.isArray(playersRes?.data)
            ? playersRes.data
            : unwrapApiList(playersRes);
        const players = rawPlayers.map(mapPlayerFromApi).filter(Boolean);
        setWcPlayers(players);
        const rawChampions =
          championsRes?.code === 0 && Array.isArray(championsRes?.data)
            ? championsRes.data
            : unwrapApiList(championsRes);
        const championRows = rawChampions
          .map((row, i) => mapTeamsChampionsRow(row, i))
          .filter(Boolean)
          .sort((a, b) => (b.votes || 0) - (a.votes || 0));
        setChampionLeaderboard(championRows);
        const rawEliminatedFirst =
          eliminatedFirstRes?.code === 0 && Array.isArray(eliminatedFirstRes?.data)
            ? eliminatedFirstRes.data
            : unwrapApiList(eliminatedFirstRes);
        const eliminatedFirstRows = rawEliminatedFirst
          .map((row, i) => mapTeamsChampionsRow(row, i))
          .filter(Boolean)
          .sort((a, b) => (b.votes || 0) - (a.votes || 0));
        setEliminatedFirstLeaderboard(eliminatedFirstRows);
        const rawMostGoalsOneMatch =
          mostGoalsOneMatchRes?.code === 0 && Array.isArray(mostGoalsOneMatchRes?.data)
            ? mostGoalsOneMatchRes.data
            : unwrapApiList(mostGoalsOneMatchRes);
        const mostGoalsOneMatchRows = rawMostGoalsOneMatch
          .map((row, i) => mapTeamsChampionsRow(row, i))
          .filter(Boolean)
          .sort((a, b) => (b.votes || 0) - (a.votes || 0));
        setMostGoalsOneMatchLeaderboard(mostGoalsOneMatchRows);
        const rawTopScorer =
          topScorerPredRes?.code === 0 && Array.isArray(topScorerPredRes?.data)
            ? topScorerPredRes.data
            : unwrapApiList(topScorerPredRes);
        const topScorerRows = rawTopScorer
          .map((row, i) => {
            const m = mapTopScorerPredictionRow(row, i);
            return m ? { m, i } : null;
          })
          .filter(Boolean)
          .sort((a, b) => {
            const dv = (b.m.votes || 0) - (a.m.votes || 0);
            if (dv !== 0) return dv;
            return a.i - b.i;
          })
          .slice(0, 10)
          .map((x) => x.m);
        setTopScorerLeaderboard(topScorerRows);
        const rawBestPlayer =
          bestPlayerPredRes?.code === 0 && Array.isArray(bestPlayerPredRes?.data)
            ? bestPlayerPredRes.data
            : unwrapApiList(bestPlayerPredRes);
        const bestPlayerRows = rawBestPlayer
          .map((row, i) => {
            const m = mapTopScorerPredictionRow(row, i);
            return m ? { m, i } : null;
          })
          .filter(Boolean)
          .sort((a, b) => {
            const dv = (b.m.votes || 0) - (a.m.votes || 0);
            if (dv !== 0) return dv;
            return a.i - b.i;
          })
          .slice(0, 10)
          .map((x) => x.m);
        setBestPlayerLeaderboard(bestPlayerRows);
      } catch (e) {
        console.error('World Cup catalog / leaderboard:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchIP = async () => {
    try {
      const res = await getIP();
      setIP(res);
    } catch (e) {}
  };

  const fetchFP = async () => {
    try {
      const res = await getFP();
      setFP(res);
    } catch (e) {}
  };

  const fetchTimeServer = async () => {
    try {
      const res = await getTime();
      setTimeServer(res);
    } catch (e) {}
  };

  const fetchDataScheduByLS = async () => {
    try {
      setIsLoading(true);
      const res2 = await getMatchList({
        site: CONST_SITE.HI88
      });

      const dataMatch2 =
        res2?.data?.length > 0
          ? res2.data.map((item) => {
              const formatCountDown = dayjs.unix(item.startTime);
              return { ...item, countdown: new Date(formatCountDown) };
            })
          : [];

      setListDataMatch(dataMatch2 || []);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching match list:', error);
      setIsLoading(false);
    }
  };

  const fetchAllSetting = async (data) => {
    try {
      setIsLoading(true);
      const res = await getAllSetting(qs.stringify({ site: CONST_SITE.HI88 }));
      setDataSettings(res?.data || []);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const TIMEOUT_DURATION = 10000; // 10 seconds

  const createPredictionsWithTimeout = (predictions) => {
    const raw = localStorage.getItem(LocalStorageKey.ACCESS_TOKEN);
    const accessToken = raw ? String(raw).replace(/"/g, '').trim() : '';
    const axiosConfig =
      accessToken !== '' ? { headers: { Authorization: `Bearer ${accessToken}` } } : {};

    return Promise.race([
      createPredictions(predictions, axiosConfig),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out')), TIMEOUT_DURATION)
      )
    ]);
  };

  const onFinishForm = async (data) => {
    try {
      const filteredMatches = getFilteredMatches();
      if (filteredMatches?.length === 0) return;

      if (!getStoredAccessToken() || !String(data?.account || '').trim()) {
        requestOpenLoginModal();
        return;
      }

      // Validate that both home and away scores are provided for each match
      const hasInvalidScores = Object.entries(data).some(([key, value]) => {
        // Bỏ qua field account và các field không phải score
        if (key === 'account' || !key.includes('_')) return false;

        const parts = key.split('_');
        if (parts.length !== 2) return false;

        const [scoreType, matchId] = parts;
        if (!['homeScore', 'awayScore'].includes(scoreType)) return false;

        const otherScoreType = scoreType === 'homeScore' ? 'awayScore' : 'homeScore';
        const otherScoreKey = `${otherScoreType}_${matchId}`;
        const otherValue = data[otherScoreKey];

        // Kiểm tra nếu một trong hai score có giá trị nhưng score kia không có
        const hasValue = value !== undefined && value !== null && value !== '';
        const hasOtherValue = otherValue !== undefined && otherValue !== null && otherValue !== '';

        if (hasValue && !hasOtherValue) {
          return true;
        }

        return false;
      });

      if (hasInvalidScores) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng nhập đầy đủ cả hai bên tỉ số cho mỗi trận đấu!'
        });
        return;
      }

      setIsLoadingSubmit(true);

      console.log('filteredMatches', filteredMatches);
      const resultGroup = groupScoresByMatchId(data, IP, FP, filteredMatches);

      if (resultGroup.length === 0) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng điền đẩy đủ thông tin và dự đoán ít nhất một trận đấu!'
        });
        setIsLoadingSubmit(false);
        return;
      }

      const res = await createPredictionsWithTimeout(resultGroup);
      if (res?.code === 0) {
        // Lưu account vào localStorage
        // if (data?.account) {
        //   localStorage.setItem('saved_account', data.account);
        // }
        notification.success({
          message: 'Thành công',
          description: 'Dự đoán tỉ số thành công!'
        });
        form.resetFields(Object.keys(form.getFieldsValue()).filter((key) => key !== 'account'));
      } else {
        if (res.valid === false) {
          notification.error({
            message: res.title_mess,
            description: res?.text_mess
          });
        } else {
          notification.error({
            message: 'Lỗi',
            description: res?.message
          });
        }
      }
      setIsLoadingSubmit(false);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error.message || 'Đã xảy ra lỗi khi gửi dự đoán!'
      });
      setIsLoadingSubmit(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    if (!getStoredAccessToken() || !String(errorInfo?.values?.account || '').trim()) {
      requestOpenLoginModal();
      return;
    }
    notification.warning({
      message: 'Cảnh báo',
      description: 'Vui lòng điền đầy đủ thông tin!'
    });
  };

  const groupScoresByMatchId = (input, ip, fp, matchInfo) => {
    const groupedMatches = Object.entries(input).reduce((acc, [key, value]) => {
      if (key === 'account') return acc;

      const parts = key.split('_');
      if (parts.length !== 2) return acc;

      const [scoreType, matchId] = parts;
      if (!matchId || !['homeScore', 'awayScore', 'sameScore'].includes(scoreType)) return acc;

      // Kiểm tra giá trị hợp lệ
      if (value === undefined || value === null || value === '') return acc;

      if (!acc[matchId]) {
        acc[matchId] = {
          matchId,
          account: input.account,
          homeScore: undefined,
          awayScore: undefined,
          sameScore: undefined
        };
      }

      if (scoreType === 'homeScore') {
        acc[matchId].homeScore = value;
      } else if (scoreType === 'awayScore') {
        acc[matchId].awayScore = value;
      } else if (scoreType === 'sameScore') {
        acc[matchId].sameScore = value;
      }

      return acc;
    }, {});

    return Object.values(groupedMatches)
      .filter(
        (match) =>
          match.homeScore !== undefined &&
          match.awayScore !== undefined &&
          match.sameScore !== undefined &&
          match.homeScore !== null &&
          match.awayScore !== null &&
          match.sameScore !== null &&
          match.homeScore !== '' &&
          match.sameScore !== '' &&
          match.awayScore !== ''
      )
      .map(({ matchId, account, homeScore, awayScore, sameScore }) => {
        const matchDetails = matchInfo.find(
          (match) => parseInt(match.matchId) === parseInt(matchId)
        );

        return {
          matchId,
          homeScore,
          awayScore,
          sameScore,
          account,
          ip,
          fp,
          homeName: matchDetails ? matchDetails.homeName : 'Unknown',
          awayName: matchDetails ? matchDetails.awayName : 'Unknown',
          site: SITE
        };
      });
  };

  const showListItemMatch = (matchList) => {
    if (!matchList || matchList.length <= 0) {
      return <p className="text-center">Hiện tại không có trận đấu nào để dự đoán</p>;
    } else {
      const filteredMatchList =
        selectedLeagues.length > 0
          ? matchList.filter((item) =>
              selectedLeagues.includes(
                leagues.find(
                  (league) =>
                    league.name === item.leagueName || item.leagueName.startsWith(league.name)
                )?.id
              )
            )
          : matchList;

      const itemMatch = filteredMatchList
        .filter((item) => timeServer <= item.countdown) // Filter out matches that have ended
        .map((item, index) => {
          // const matchDayOfWeek = daysOfWeek[dayjs(item.startTime * 1000).format('dddd')]; // Calculate day of week for each match
          return (
            <div
              className={twMerge(
                'flex items-center max-w-[1200px] xl:w-3/4 md:w-4/5 w-full mx-auto bg-[#FEFEFE] rounded-2xl px-1 sm:px-2 pb-2 text-sm font-medium gap-2 border-solid border-6 border-[#26A9E0] flex-col shadow-[0_0_10.574px_0_rgba(0,0,0,0.09)_inset] mt-3',
                timeServer > item.countdown ? 'contrast-50' : ''
              )}
              key={index}
            >
              <div
                className={twMerge(
                  'italic text-center min-w-[300px]',
                  'px-5 md:px-10 py-2 bg-[#26A9E0] rounded-b-[16px]'
                )}
              >
                <p className="text-white text-center font-roboto font-bold leading-normal not-italic text-xs sm:text-sm md:text-base lg:text-lg">
                  {item?.leagueName}
                </p>
              </div>
              <div className="flex flex-row justify-center md:justify-center gap-1 md:gap-2 w-full items-center text-xs sm:text-sm md:text-base pb-3">
                <div className="flex flex-row items-center w-full md:w-[330px] h-[44px] md:h-[50px] justify-center">
                  <span
                    className="flex flex-row h-full justify-center items-center
                         px-[2px] md:px-4 py-[5px] gap-[2px]
                         text-[10px] sm:text-[12px] md:text-[14px]
                        
                        flex-none order-0 grow-0
                        w-full"
                  >
                    <span
                      className={`flex items-center justify-center h-full md:text-[14px] sm:text-[12px] text-[10px] ${
                        timeServer > item.countdown ? 'text-red-500' : 'text-black'
                      }`}
                    >
                      {timeServer > item.countdown ? 'Hết giờ' : 'Thời gian dự đoán còn: '}
                    </span>

                    <Countdown
                      date={item.countdown}
                      renderer={({ days, hours, minutes, seconds, completed }) => {
                        if (completed) {
                          return <span></span>;
                        } else {
                          const pad = (n) => n.toString().padStart(2, '0');
                          const totalHours = days * 24 + hours;
                          return (
                            <span className="text-[#26A9E0] font-semibold">
                              {pad(totalHours)}:{pad(minutes)}:{pad(seconds)}
                            </span>
                          );
                        }
                      }}
                    />

                    {/* <Statistic.Countdown
                      value={item.countdown}
                      onFinish={onFinishCountDown}
                      className="text-[#0053DF] md:text-[14px] sm:text-[12px] text-[10px] ml-1 font-semibold flex items-center justify-center"
                    /> */}
                  </span>
                </div>
                <div className="flex flex-row items-center w-full md:w-[330px]  h-[44px] md:h-[50px] justify-center">
                  <span
                    className="flex flex-row h-full justify-center items-center
                        px-[2px] md:px-4 py-[5px] gap-[2px]
                        text-[10px] md:text-[14px] sm:text-[12px] 
                        flex-none order-0 grow-0
                        w-full"
                  >
                    <span className="flex items-center justify-center h-full ">
                      Thời gian kết thúc:{' '}
                    </span>
                    <span className="text-[#26A9E0] ml-1 font-semibold flex items-center h-full justify-center">
                      {dayjs(item.startTime * 1000).format('HH:mm')}
                    </span>
                  </span>
                </div>
              </div>
              {/* <div className="rounded-t-[5.287px] bg-[linear-gradient(180deg,#254680_0%,#26A9E0_54.9%,#2664d1_100%)] flex w-auto min-w-[180px] sm:w-[210px] h-[28px] sm:h-[32px] px-3 sm:px-4 py-1.5 sm:py-2 justify-center items-center shrink-0">
                <p className="text-white text-center text-xs sm:text-sm md:text-base font-semibold leading-normal whitespace-nowrap">
                  {`${matchDayOfWeek}, ${dayjs(item.startTime * 1000).format('DD/MM/YYYY')}`}
                </p>
              </div> */}
              <div className="flex flex-row item-center justify-center h-[70px] md:h-[90px] w-full xl:w-3/4 relative">
                <div
                  className="absolute w-[95%] h-[60%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 !z-0 bg-[#26A9E0]
                                    border-[2px]
                                    shadow-[inset_0_0_20px_0_#0C2E68]
                                    rounded-lg"
                  style={{
                    borderImageSource: 'linear-gradient(0deg, #AEC8F5 0%, #FFFFFF 100%)',
                    borderImageSlice: 1
                  }}
                ></div>
                <div
                  className="z-[1] flex justify-center items-center w-full rounded-full bg-white shadow-[inset_0_0_5.43px_0_#00000040] 
                                    md:max-w-[90px] md:h-[90px] md:p-[5px]
                                    max-w-[60px] h-[60px] p-[2px]"
                  style={{ border: '1px solid #26A9E0' }}
                >
                  <img src={item.homeLogo} className="w-[75%] h-full object-contain" alt="" />
                </div>

                <div className="text-xs sm:text-sm md:text-base font-medium flex justify-center items-center w-full h-full z-[1] text-white px-1">
                  {item.homeName}
                </div>

                <div className="flex flex-row items-center justify-center w-[60%] z-[1]">
                  <img
                    src="/assets/images/match/vs.png"
                    className="w-[100%] h-full object-contain"
                    alt=""
                  />
                </div>
                <div className="text-xs sm:text-sm md:text-base font-medium flex justify-center items-center w-full h-full z-[1] text-white px-1">
                  {item.awayName}
                </div>

                <div
                  className="z-[1] flex flex-col justify-center items-center w-full rounded-full bg-white shadow-[inset_0_0_5.43px_0_#00000040] 
                                    md:max-w-[90px] md:h-[90px] md:p-[5px]
                                    max-w-[60px] h-[60px] p-[2px]"
                  style={{ border: '1px solid #26A9E0' }}
                >
                  <img src={item.awayLogo} className="w-[75%] h-full object-contain" alt="" />
                </div>
              </div>
              <div className="flex flex-col w-full sm:w-3/4 justify-center items-center px-2">
                <div className="flex md:justify-around justify-center flex-row items-center p-1 gap-3 sm:gap-5 md:gap-16">
                  <Form.Item className="m-0 p-0" name={`homeScore_${item.matchId}`}>
                    <InputNumber
                      disabled={timeServer > item.countdown ? true : false}
                      controls={false}
                      min={0}
                      max={99}
                      maxLength={2}
                      placeholder="Nhập số"
                      className="bg-white border-0 w-[70px] sm:w-[90px] md:w-[145px] h-[34px] sm:h-[37px] md:h-[40px] text-center text-xs sm:text-sm md:text-base font-normal text-[#505050] shadow-[0px_1.057px_4.23px_0px_inset_rgba(0,0,0,0.29)] rounded-md"
                      style={{
                        textAlign: 'center',
                        fontFamily: "'Roboto', sans-serif"
                      }}
                    />
                  </Form.Item>
                  <span className="font-bold text-black text-sm sm:text-base md:text-lg whitespace-nowrap px-1">
                    Tỉ số
                  </span>
                  <Form.Item className="m-0 p-0" name={`awayScore_${item.matchId}`}>
                    <InputNumber
                      disabled={timeServer > item.countdown ? true : false}
                      controls={false}
                      min={0}
                      max={99}
                      maxLength={2}
                      placeholder="Nhập số"
                      className="bg-white border-0 w-[70px] sm:w-[90px] md:w-[145px] h-[34px] sm:h-[37px] md:h-[40px] text-center text-xs sm:text-sm md:text-base font-normal text-[#505050] shadow-[0px_1.057px_4.23px_0px_inset_rgba(0,0,0,0.29)] rounded-md"
                      style={{
                        textAlign: 'center',
                        fontFamily: "'Roboto', sans-serif"
                      }}
                    />
                  </Form.Item>
                </div>
                <div className="flex justify-between flex-row items-center p-1 gap-16">
                  <Form.Item className="m-0 p-0" name={`sameScore_${item.matchId}`}>
                    <InputNumber
                      disabled={timeServer > item.countdown ? true : false}
                      controls={false}
                      min={0}
                      maxLength={5}
                      placeholder="Số người dự đoán giống bạn"
                      className="bg-white border-0 !w-[290px] h-[37px] text-center text-[14.804px] font-normal text-[#505050] shadow-[0px_1.057px_4.23px_0px_inset_rgba(0,0,0,0.29)]"
                      style={{
                        textAlign: 'center',
                        fontFamily: "'Roboto', sans-serif"
                      }}
                    />
                  </Form.Item>
                </div>
              </div>
              <div className="flex flex-row w-full sm:w-3/4 justify-center mt-2">
                <Button
                  type="none"
                  htmlType="submit"
                  className="
                    w-[140px] sm:w-[163px]
                    h-[38px] sm:h-[40px] md:h-[44px]
                    border-0 shadow-none bg-transparent hover:bg-transparent focus:bg-transparent hover:scale-105 transition-all duration-200"
                  style={{
                    backgroundImage: `url(assets/images/match/btn_send.png)`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                />
              </div>
              <div className="flex flex-row items-center w-full md:w-[330px] justify-center">
                <span
                  className="flex flex-row h-full justify-center items-center
                        px-2 gap-[3px]
                        text-black"
                >
                  <span className="flex items-center justify-center h-full md:text-[16px] text-[14px]">
                    Trước{' '}
                  </span>
                  <span className="text-[#26A9E0] ml-1 font-semibold flex items-center h-full justify-center">
                    {dayjs(item.startTime * 1000).format('HH:mm')}
                  </span>
                  <span className="flex items-center justify-center h-full md:text-[16px] text-[14px]">
                    ngày{' '}
                  </span>
                  <span className="text-[#26A9E0] ml-1 font-semibold flex items-center h-full justify-center">
                    {dayjs(item.startTime * 1000).format('DD/MM/YYYY')}
                  </span>
                </span>
              </div>
            </div>
          );
        });

      if (!itemMatch || itemMatch.length <= 0) {
        return <p className="text-center">Hiện tại không có trận đấu nào để dự đoán</p>;
      }
      return itemMatch;
    }
  };

  const filteredChampionTeams = isChampionAutoFilled
    ? championTeams
    : championTeams.filter((t) =>
        t.name.toLowerCase().includes(championQuery.trim().toLowerCase())
      );

  const selectedChampion =
    championTeams.find((t) => String(t.id) === String(selectedChampionId)) || null;

  const filteredEliminatedFirstTeams = isEliminatedFirstAutoFilled
    ? championTeams
    : championTeams.filter((t) =>
        t.name.toLowerCase().includes(eliminatedFirstQuery.trim().toLowerCase())
      );
  const selectedEliminatedFirst =
    championTeams.find((t) => String(t.id) === String(selectedEliminatedFirstId)) || null;

  const filteredMostGoalsOneMatchTeams = isMostGoalsOneMatchAutoFilled
    ? championTeams
    : championTeams.filter((t) =>
        t.name.toLowerCase().includes(mostGoalsOneMatchQuery.trim().toLowerCase())
      );
  const selectedMostGoalsOneMatch =
    championTeams.find((t) => String(t.id) === String(selectedMostGoalsOneMatchId)) || null;

  const filteredRound16FirstTeams = isRound16FirstAutoFilled
    ? championTeams
    : championTeams.filter((t) =>
        t.name.toLowerCase().includes(round16FirstQuery.trim().toLowerCase())
      );
  const selectedRound16First =
    championTeams.find((t) => String(t.id) === String(selectedRound16FirstId)) || null;

  const filteredTopScorerPlayers = isTopScorerAutoFilled
    ? topScorerPlayersList
    : topScorerPlayersList.filter((p) =>
        p.name.toLowerCase().includes(topScorerQuery.trim().toLowerCase())
      );
  const selectedTopScorer =
    topScorerPlayersList.find((p) => String(p.id) === String(selectedTopScorerId)) || null;

  const filteredBestPlayerPlayers = isBestPlayerAutoFilled
    ? bestPlayerPlayersList
    : bestPlayerPlayersList.filter((p) =>
        p.name.toLowerCase().includes(bestPlayerQuery.trim().toLowerCase())
      );
  const selectedBestPlayer =
    bestPlayerPlayersList.find((p) => String(p.id) === String(selectedBestPlayerId)) || null;

  const isVoteSubmitting =
    isChampionVoting ||
    isEliminatedFirstVoting ||
    isMostGoalsOneMatchVoting ||
    isRound16FirstVoting ||
    isTopScorerVoting ||
    isBestPlayerVoting;

  const closeVoteConfirm = () => {
    if (isVoteSubmitting) return;
    setVoteConfirm({
      open: false,
      type: '',
      title: '',
      selectedName: '',
      payload: null,
      error: ''
    });
  };

  const handleConfirmVote = async () => {
    if (!voteConfirm?.type || !voteConfirm?.payload) return;

    if (voteConfirm.type === 'champion') {
      setIsChampionVoting(true);
      try {
        const res = await voteWorldCupChampion(voteConfirm.payload);
        const ok = res?.code === 0 || res?.code === 200 || res?.success === true;

        if (ok) {
          localStorage.setItem('prediction_champion', voteConfirm.payload.teamId);
          notification.success({
            message: 'Thành công',
            description: `Bạn đã dự đoán: ${voteConfirm.payload.teamName}`
          });

          const championsRes = await getTeamsChampions();
          const rawChampions =
            championsRes?.code === 0 && Array.isArray(championsRes?.data)
              ? championsRes.data
              : unwrapApiList(championsRes);
          const championRows = rawChampions
            .map((row, i) => mapTeamsChampionsRow(row, i))
            .filter(Boolean)
            .sort((a, b) => (b.votes || 0) - (a.votes || 0));
          setChampionLeaderboard(championRows);
          closeVoteConfirm();
        } else {
          setVoteConfirm((prev) => ({
            ...prev,
            error: res?.text_mess || res?.message || 'Không thể bình chọn đội vô địch.'
          }));
        }
      } catch (e) {
        setVoteConfirm((prev) => ({
          ...prev,
          error: e?.message || 'Không thể bình chọn đội vô địch.'
        }));
      } finally {
        setIsChampionVoting(false);
      }
      return;
    }

    if (voteConfirm.type === 'eliminated-first') {
      setIsEliminatedFirstVoting(true);
      try {
        const res = await voteWorldCupEliminatedFirst(voteConfirm.payload);
        const ok = res?.code === 0 || res?.code === 200 || res?.success === true;

        if (ok) {
          localStorage.setItem('prediction_eliminated_first', voteConfirm.payload.teamId);
          notification.success({
            message: 'Thành công',
            description: `Bạn đã dự đoán: ${voteConfirm.payload.teamName}`
          });

          const eliminatedRes = await getTeamsEliminatedFirst();
          const rawEliminated =
            eliminatedRes?.code === 0 && Array.isArray(eliminatedRes?.data)
              ? eliminatedRes.data
              : unwrapApiList(eliminatedRes);
          const eliminatedRows = rawEliminated
            .map((row, i) => mapTeamsChampionsRow(row, i))
            .filter(Boolean)
            .sort((a, b) => (b.votes || 0) - (a.votes || 0));
          setEliminatedFirstLeaderboard(eliminatedRows);
          closeVoteConfirm();
        } else {
          setVoteConfirm((prev) => ({
            ...prev,
            error: res?.text_mess || res?.message || 'Không thể gửi dự đoán đội đầu tiên bị loại.'
          }));
        }
      } catch (e) {
        setVoteConfirm((prev) => ({
          ...prev,
          error: e?.message || 'Không thể gửi dự đoán đội đầu tiên bị loại.'
        }));
      } finally {
        setIsEliminatedFirstVoting(false);
      }
      return;
    }

    if (voteConfirm.type === 'most-goals-in-one-match') {
      setIsMostGoalsOneMatchVoting(true);
      try {
        const res = await voteWorldCupMostGoalsInOneMatch(voteConfirm.payload);
        const ok = res?.code === 0 || res?.code === 200 || res?.success === true;

        if (ok) {
          localStorage.setItem('prediction_most_goals_in_one_match', voteConfirm.payload.teamId);
          notification.success({
            message: 'Thành công',
            description: `Bạn đã dự đoán: ${voteConfirm.payload.teamName}`
          });

          const mostGoalsRes = await getTeamsMostGoalsInOneMatch();
          const rawMostGoals =
            mostGoalsRes?.code === 0 && Array.isArray(mostGoalsRes?.data)
              ? mostGoalsRes.data
              : unwrapApiList(mostGoalsRes);
          const mostGoalsRows = rawMostGoals
            .map((row, i) => mapTeamsChampionsRow(row, i))
            .filter(Boolean)
            .sort((a, b) => (b.votes || 0) - (a.votes || 0));
          setMostGoalsOneMatchLeaderboard(mostGoalsRows);
          closeVoteConfirm();
        } else {
          setVoteConfirm((prev) => ({
            ...prev,
            error:
              res?.text_mess ||
              res?.message ||
              'Không thể gửi dự đoán đội có nhiều bàn thắng nhất 1 trận.'
          }));
        }
      } catch (e) {
        setVoteConfirm((prev) => ({
          ...prev,
          error: e?.message || 'Không thể gửi dự đoán đội có nhiều bàn thắng nhất 1 trận.'
        }));
      } finally {
        setIsMostGoalsOneMatchVoting(false);
      }
      return;
    }

    // if (voteConfirm.type === 'round16-first') {
    //   setIsRound16FirstVoting(true);
    //   try {
    //     const res = await voteWorldCupRound16First(voteConfirm.payload);
    //     const ok = res?.code === 0 || res?.code === 200 || res?.success === true;

    //     if (ok) {
    //       localStorage.setItem('prediction_round16_first', voteConfirm.payload.teamId);
    //       notification.success({
    //         message: 'Thành công',
    //         description: `Bạn đã dự đoán: ${voteConfirm.payload.teamName}`
    //       });

    //       const round16Res = await getTeamsRound16First();
    //       const rawRound16 =
    //         round16Res?.code === 0 && Array.isArray(round16Res?.data)
    //           ? round16Res.data
    //           : unwrapApiList(round16Res);
    //       const round16Rows = rawRound16
    //         .map((row, i) => mapTeamsChampionsRow(row, i))
    //         .filter(Boolean)
    //         .sort((a, b) => (b.votes || 0) - (a.votes || 0));
    //       setRound16FirstLeaderboard(round16Rows);
    //       closeVoteConfirm();
    //     } else {
    //       setVoteConfirm((prev) => ({
    //         ...prev,
    //         error:
    //           res?.text_mess ||
    //           res?.message ||
    //           'Không thể gửi dự đoán đội đầu tiên vào vòng 16.'
    //       }));
    //     }
    //   } catch (e) {
    //     setVoteConfirm((prev) => ({
    //       ...prev,
    //       error: e?.message || 'Không thể gửi dự đoán đội đầu tiên vào vòng 16.'
    //     }));
    //   } finally {
    //     setIsRound16FirstVoting(false);
    //   }
    //   return;
    // }

    if (voteConfirm.type === 'top-scorer') {
      setIsTopScorerVoting(true);
      try {
        const res = await voteWorldCupTopScorer(voteConfirm.payload);
        const ok = res?.code === 0 || res?.code === 200 || res?.success === true;

        if (ok) {
          localStorage.setItem('prediction_top_scorer', voteConfirm.payload.playerId);
          notification.success({
            message: 'Thành công',
            description: `Bạn đã dự đoán: ${voteConfirm.payload.playerName}`
          });

          const topScorerPredRes = await getTopScorerPredictions({
            params: { site: CONST_SITE.HI88 }
          });
          const rawTopScorer =
            topScorerPredRes?.code === 0 && Array.isArray(topScorerPredRes?.data)
              ? topScorerPredRes.data
              : unwrapApiList(topScorerPredRes);
          const topScorerRows = rawTopScorer
            .map((row, i) => {
              const m = mapTopScorerPredictionRow(row, i);
              return m ? { m, i } : null;
            })
            .filter(Boolean)
            .sort((a, b) => {
              const dv = (b.m.votes || 0) - (a.m.votes || 0);
              if (dv !== 0) return dv;
              return a.i - b.i;
            })
            .slice(0, 10)
            .map((x) => x.m);

          setTopScorerLeaderboard(topScorerRows);
          closeVoteConfirm();
        } else {
          setVoteConfirm((prev) => ({
            ...prev,
            error: res?.text_mess || res?.message || 'Không thể bình chọn vua phá lưới.'
          }));
        }
      } catch (e) {
        setVoteConfirm((prev) => ({
          ...prev,
          error: e?.message || 'Không thể bình chọn vua phá lưới.'
        }));
      } finally {
        setIsTopScorerVoting(false);
      }
      return;
    }

    if (voteConfirm.type === 'best-player') {
      setIsBestPlayerVoting(true);
      try {
        const res = await voteWorldCupBestPlayer(voteConfirm.payload);
        const ok = res?.code === 0 || res?.code === 200 || res?.success === true;

        if (ok) {
          localStorage.setItem('prediction_best_player', voteConfirm.payload.playerId);
          notification.success({
            message: 'Thành công',
            description: `Bạn đã dự đoán: ${voteConfirm.payload.playerName}`
          });

          const bestPlayerPredRes = await getBestPlayerPredictions({
            params: { site: CONST_SITE.HI88 }
          });
          const rawBestPlayer =
            bestPlayerPredRes?.code === 0 && Array.isArray(bestPlayerPredRes?.data)
              ? bestPlayerPredRes.data
              : unwrapApiList(bestPlayerPredRes);
          const bestPlayerRows = rawBestPlayer
            .map((row, i) => {
              const m = mapTopScorerPredictionRow(row, i);
              return m ? { m, i } : null;
            })
            .filter(Boolean)
            .sort((a, b) => {
              const dv = (b.m.votes || 0) - (a.m.votes || 0);
              if (dv !== 0) return dv;
              return a.i - b.i;
            })
            .slice(0, 10)
            .map((x) => x.m);

          setBestPlayerLeaderboard(bestPlayerRows);
          closeVoteConfirm();
        } else {
          setVoteConfirm((prev) => ({
            ...prev,
            error: res?.text_mess || res?.message || 'Không thể bình chọn cầu thủ xuất sắc nhất.'
          }));
        }
      } catch (e) {
        setVoteConfirm((prev) => ({
          ...prev,
          error: e?.message || 'Không thể bình chọn cầu thủ xuất sắc nhất.'
        }));
      } finally {
        setIsBestPlayerVoting(false);
      }
    }
  };

  const renderChampionTab = () => {
    const onSubmitChampion = () => {
      if (!selectedChampion) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng chọn 1 đội để dự đoán.'
        });
        return;
      }
      if (!IP || !FP) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Đang tải thông tin IP/FP, vui lòng thử lại sau.'
        });
        return;
      }

      setVoteConfirm({
        open: true,
        type: 'champion',
        title: 'Đội vô địch',
        selectedName: selectedChampion.name,
        payload: {
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP,
          site: SITE,
          teamName: selectedChampion.name,
          teamId: selectedChampion.id
        },
        error: ''
      });
    };

    return (
      <ChampionPredictionTab
        championLeaderboard={championLeaderboard}
        championTeams={championTeams}
        filteredChampionTeams={filteredChampionTeams}
        championQuery={championQuery}
        setChampionQuery={(value) => {
          setIsChampionAutoFilled(false);
          setChampionQuery(value);
        }}
        isChampionVoting={isChampionVoting}
        onSubmitChampion={onSubmitChampion}
        selectedChampionId={selectedChampionId}
        setSelectedChampionId={(id, prefillName) => {
          setSelectedChampionId(id);
          const team = championTeams.find((t) => String(t.id) === String(id));
          if (team?.name) {
            setChampionQuery(team.name);
            setIsChampionAutoFilled(true);
          } else if (prefillName) {
            setChampionQuery(prefillName);
            setIsChampionAutoFilled(true);
          }
        }}
      />
    );
  };

  const renderEliminatedFirstTab = () => {
    const onSubmitEliminatedFirst = () => {
      if (!selectedEliminatedFirst) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng chọn 1 đội để dự đoán.'
        });
        return;
      }
      if (!IP || !FP) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Đang tải thông tin IP/FP, vui lòng thử lại sau.'
        });
        return;
      }

      setVoteConfirm({
        open: true,
        type: 'eliminated-first',
        title: 'Đội đầu tiên bị loại',
        selectedName: selectedEliminatedFirst.name,
        payload: {
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP,
          site: SITE,
          teamName: selectedEliminatedFirst.name,
          teamId: selectedEliminatedFirst.id
        },
        error: ''
      });
    };

    return (
      <ChampionPredictionTab
        panelTitle="Dự đoán đội đầu tiên bị loại"
        searchPlaceholder="Tìm tên đội..."
        emptyText="Không tìm thấy đội phù hợp."
        championLeaderboard={eliminatedFirstLeaderboard}
        championTeams={championTeams}
        filteredChampionTeams={filteredEliminatedFirstTeams}
        championQuery={eliminatedFirstQuery}
        setChampionQuery={(value) => {
          setIsEliminatedFirstAutoFilled(false);
          setEliminatedFirstQuery(value);
        }}
        isChampionVoting={isEliminatedFirstVoting}
        onSubmitChampion={onSubmitEliminatedFirst}
        selectedChampionId={selectedEliminatedFirstId}
        setSelectedChampionId={(id, prefillName) => {
          setSelectedEliminatedFirstId(id);
          const team = championTeams.find((t) => String(t.id) === String(id));
          if (team?.name) {
            setEliminatedFirstQuery(team.name);
            setIsEliminatedFirstAutoFilled(true);
          } else if (prefillName) {
            setEliminatedFirstQuery(prefillName);
            setIsEliminatedFirstAutoFilled(true);
          }
        }}
      />
    );
  };

  const renderRound16FirstTab = () => {
    const onSubmitRound16First = () => {
      if (!selectedRound16First) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng chọn 1 đội để dự đoán.'
        });
        return;
      }
      if (!IP || !FP) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Đang tải thông tin IP/FP, vui lòng thử lại sau.'
        });
        return;
      }

      setVoteConfirm({
        open: true,
        type: 'round16-first',
        title: 'Đội đầu tiên vào vòng 16',
        selectedName: selectedRound16First.name,
        payload: {
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP,
          site: SITE,
          teamName: selectedRound16First.name,
          teamId: selectedRound16First.id
        },
        error: ''
      });
    };

    return (
      <ChampionPredictionTab
        panelTitle="Dự đoán đội đầu tiên vào vòng 16"
        searchPlaceholder="Tìm tên đội..."
        emptyText="Không tìm thấy đội phù hợp."
        championLeaderboard={round16FirstLeaderboard}
        championTeams={championTeams}
        filteredChampionTeams={filteredRound16FirstTeams}
        championQuery={round16FirstQuery}
        setChampionQuery={(value) => {
          setIsRound16FirstAutoFilled(false);
          setRound16FirstQuery(value);
        }}
        isChampionVoting={isRound16FirstVoting}
        onSubmitChampion={onSubmitRound16First}
        selectedChampionId={selectedRound16FirstId}
        setSelectedChampionId={(id, prefillName) => {
          setSelectedRound16FirstId(id);
          const team = championTeams.find((t) => String(t.id) === String(id));
          if (team?.name) {
            setRound16FirstQuery(team.name);
            setIsRound16FirstAutoFilled(true);
          } else if (prefillName) {
            setRound16FirstQuery(prefillName);
            setIsRound16FirstAutoFilled(true);
          }
        }}
      />
    );
  };

  const renderMostGoalsOneMatchTab = () => {
    const onSubmitMostGoalsOneMatch = () => {
      if (!selectedMostGoalsOneMatch) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng chọn 1 đội để dự đoán.'
        });
        return;
      }
      if (!IP || !FP) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Đang tải thông tin IP/FP, vui lòng thử lại sau.'
        });
        return;
      }

      setVoteConfirm({
        open: true,
        type: 'most-goals-in-one-match',
        title: 'Đội có nhiều bàn thắng nhất 1 trận',
        selectedName: selectedMostGoalsOneMatch.name,
        payload: {
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP,
          site: SITE,
          teamName: selectedMostGoalsOneMatch.name,
          teamId: selectedMostGoalsOneMatch.id
        },
        error: ''
      });
    };

    return (
      <ChampionPredictionTab
        panelTitle="Dự đoán đội có nhiều bàn thắng nhất 1 trận"
        searchPlaceholder="Tìm tên đội..."
        emptyText="Không tìm thấy đội phù hợp."
        championLeaderboard={mostGoalsOneMatchLeaderboard}
        championTeams={championTeams}
        filteredChampionTeams={filteredMostGoalsOneMatchTeams}
        championQuery={mostGoalsOneMatchQuery}
        setChampionQuery={(value) => {
          setIsMostGoalsOneMatchAutoFilled(false);
          setMostGoalsOneMatchQuery(value);
        }}
        isChampionVoting={isMostGoalsOneMatchVoting}
        onSubmitChampion={onSubmitMostGoalsOneMatch}
        selectedChampionId={selectedMostGoalsOneMatchId}
        setSelectedChampionId={(id, prefillName) => {
          setSelectedMostGoalsOneMatchId(id);
          const team = championTeams.find((t) => String(t.id) === String(id));
          if (team?.name) {
            setMostGoalsOneMatchQuery(team.name);
            setIsMostGoalsOneMatchAutoFilled(true);
          } else if (prefillName) {
            setMostGoalsOneMatchQuery(prefillName);
            setIsMostGoalsOneMatchAutoFilled(true);
          }
        }}
      />
    );
  };

  const renderTopScorerTab = ({
    panelTitle = 'Dự đoán vua phá lưới',
    confirmTitle = 'Vua phá lưới'
  } = {}) => {
    const onSubmitTopScorer = () => {
      if (!selectedTopScorer) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng chọn 1 cầu thủ để dự đoán.'
        });
        return;
      }
      if (!IP || !FP) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Đang tải thông tin IP/FP, vui lòng thử lại sau.'
        });
        return;
      }

      setVoteConfirm({
        open: true,
        type: 'top-scorer',
        title: confirmTitle,
        selectedName: selectedTopScorer.name,
        payload: {
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP,
          site: SITE,
          playerName: selectedTopScorer.name,
          teamName: selectedTopScorer.teamName,
          playerId: selectedTopScorer.id
        },
        error: ''
      });
    };

    return (
      <PlayerPredictionTab
        title={panelTitle}
        leaderboard={topScorerLeaderboard}
        players={filteredTopScorerPlayers}
        suggestionPlayers={topScorerPlayersList}
        query={topScorerQuery}
        setQuery={(value) => {
          setIsTopScorerAutoFilled(false);
          setTopScorerQuery(value);
        }}
        selectedId={selectedTopScorerId}
        setSelectedId={(id) => {
          setSelectedTopScorerId(id);
          const player = topScorerPlayersList.find((p) => String(p.id) === String(id));
          if (player?.name) {
            setTopScorerQuery(player.name);
            setIsTopScorerAutoFilled(true);
          }
        }}
        loading={isTopScorerVoting}
        onSubmit={onSubmitTopScorer}
      />
    );
  };

  const renderBestPlayerTab = () => {
    const onSubmitBestPlayer = () => {
      if (!selectedBestPlayer) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng chọn 1 cầu thủ để dự đoán.'
        });
        return;
      }
      if (!IP || !FP) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Đang tải thông tin IP/FP, vui lòng thử lại sau.'
        });
        return;
      }

      setVoteConfirm({
        open: true,
        type: 'best-player',
        title: 'Cầu thủ xuất sắc nhất',
        selectedName: selectedBestPlayer.name,
        payload: {
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP,
          site: SITE,
          playerName: selectedBestPlayer.name,
          teamName: selectedBestPlayer.teamName,
          playerId: selectedBestPlayer.id
        },
        error: ''
      });
    };

    return (
      <PlayerPredictionTab
        title="Dự đoán cầu thủ xuất sắc nhất"
        leaderboard={bestPlayerLeaderboard}
        players={filteredBestPlayerPlayers}
        suggestionPlayers={bestPlayerPlayersList}
        query={bestPlayerQuery}
        setQuery={(value) => {
          setIsBestPlayerAutoFilled(false);
          setBestPlayerQuery(value);
        }}
        selectedId={selectedBestPlayerId}
        setSelectedId={(id) => {
          setSelectedBestPlayerId(id);
          const player = bestPlayerPlayersList.find((p) => String(p.id) === String(id));
          if (player?.name) {
            setBestPlayerQuery(player.name);
            setIsBestPlayerAutoFilled(true);
          }
        }}
        loading={isBestPlayerVoting}
        onSubmit={onSubmitBestPlayer}
      />
    );
  };

  const renderOtherWcPlaceholder = (label) => {
    return (
      <div className="w-full py-12 flex flex-col items-center justify-center">
        <InboxOutlined className="text-5xl text-[#94a3b8] mb-3" />
        <div className="text-sm lg:text-base text-[#64748b] text-center px-4">
          Nội dung <span className="font-extrabold text-[#0f172a]">{label}</span> đang được cập
          nhật.
        </div>
      </div>
    );
  };

  const renderWorldCupSection = () => {
    const activeLabel = wcPredictionTabs.find((t) => t.key === activeWcTab)?.label;

    const scrollWcTabs = (direction) => {
      const el = wcTabsScrollRef.current;
      if (!el) return;
      const delta = direction === 'next' ? 280 : -280;
      el.scrollBy({ left: delta, behavior: 'smooth' });
    };

    /** Figma 72-910: từ md — 18px / px-4; dưới md — chữ & padding nhỏ hơn */
    const tabBtnBase =
      'shrink-0 rounded-md px-2.5 py-2 border-0 outline-none appearance-none ' +
      'font-roboto font-black text-[12px] leading-[1.2] text-white uppercase ' +
      'tracking-normal whitespace-nowrap transition-[filter,transform] duration-200 ' +
      'hover:brightness-110 active:scale-[0.98] ' +
      'focus-visible:ring-2 focus-visible:ring-[#26A9E0] focus-visible:ring-offset-1 ' +
      'md:rounded-lg md:px-4 md:py-4 md:text-[18px] md:leading-[1.171875] md:tracking-wide ' +
      'md:focus-visible:ring-offset-2';

    const gradActive = { background: 'linear-gradient(180deg, #26A9E0 0%, #0B85C7 100%)' };
    const gradInactive = { background: 'linear-gradient(180deg, #273F48 0%, #152B33 100%)' };

    const navArrowClass =
      'flex h-8 w-8 shrink-0 items-center justify-center rounded-md border-0 outline-none ' +
      'bg-transparent shadow-none ' +
      'transition hover:opacity-80 active:scale-95 focus-visible:ring-2 focus-visible:ring-[#26A9E0] ' +
      'md:h-10 md:w-10 md:rounded-lg';

    return (
      <div className="w-full">
        {/* Figma 72-910: icon prev/next 24px, gap 16px, nút rounded 8px padding 16px */}
        <div className="flex w-full items-center gap-1 md:gap-3">
          <button
            type="button"
            aria-label="Cuộn tab trái"
            onClick={() => scrollWcTabs('prev')}
            className={navArrowClass}
          >
            <img
              src={wcTabPrev}
              alt=""
              className="h-5 w-5 object-contain md:h-6 md:w-6"
              draggable={false}
            />
          </button>

          <div
            ref={wcTabsScrollRef}
            className="flex min-h-0 min-w-0 flex-1 items-center justify-center gap-2 overflow-x-auto overflow-y-hidden py-0.5 md:gap-4 md:py-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {wcPredictionTabs.map((tab) => {
              const isActive = activeWcTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveWcTab(tab.key)}
                  className={tabBtnBase}
                  style={isActive ? gradActive : gradInactive}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            aria-label="Cuộn tab phải"
            onClick={() => scrollWcTabs('next')}
            className={navArrowClass}
          >
            <img
              src={wcTabNext}
              alt=""
              className="h-5 w-5 object-contain md:h-6 md:w-6"
              draggable={false}
            />
          </button>
        </div>

        <div className="mt-4">
          {activeWcTab === 'champion'
            ? renderChampionTab()
            : activeWcTab === 'first-eliminated'
              ? renderEliminatedFirstTab()
              : activeWcTab === 'most-goals-in-one-match'
                ? renderMostGoalsOneMatchTab()
                : // : activeWcTab === 'first-to-round16'
                  //   ? renderRound16FirstTab()
                  activeWcTab === 'top-scorer'
                  ? renderTopScorerTab()
                  : activeWcTab === 'best-player'
                    ? renderBestPlayerTab()
                    : renderOtherWcPlaceholder(activeLabel)}
        </div>
        <div className="mt-0 md:mt-8">
          <Rule />
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-full flex flex-col">
      <img
        src={bg_home}
        alt="Background"
        className="w-full h-full fixed right-0 bottom-0 object-cover -z-10"
      />
      <div className="hidden xl:block">
        <div className="fixed left-0 top-4">
          <img
            src="assets/images/match/terry1.png"
            alt="Background"
            className="w-80 h-auto object-contain"
          />
        </div>
        <div className="fixed right-0 bottom-0">
          <img
            src="assets/images/match/terry2.png"
            alt="Background"
            className="w-60 h-auto object-contain"
          />
        </div>
      </div>
      <div className="relative z-10">
        <HeaderMenu />
        <div className="mx-auto w-full md:pb-2 px-2 max-w-[1200px]">
          <div className="mt-2 lg:mt-4 rounded-[24px] border-2 border-[#26A9E0] bg-white overflow-hidden shadow-[0_0_0_4px_rgba(38,169,224,0.12)]">
            {/* Tab chính — mobile Figma 87-353: lề ngang đều (px-4) + cụm tab max 318px căn giữa; md+ full width row căn giữa */}
            <div className="overflow-x-hidden overflow-y-visible px-4 pt-0 md:flex md:justify-center md:overflow-x-auto md:px-0 md:[scrollbar-width:thin]">
              <div className="mx-auto flex w-full max-w-[318px] min-w-0 items-stretch justify-center gap-0 md:mx-0 md:inline-flex md:max-w-none md:w-auto md:shrink-0 md:self-start">
                <button
                  type="button"
                  onClick={() => setMainHomeTab('score')}
                  className={twMerge(
                    'box-border min-w-0 flex-1 md:w-[300px] md:max-w-[min(300px,100vw)] md:flex-none md:shrink-0',
                    'flex h-[44px] items-center justify-center gap-1.5 px-2 py-1.5 text-center md:h-[52px] md:gap-2.5 md:px-10 md:py-2',
                    'font-roboto font-black uppercase text-[13px] leading-[1.2] text-white md:text-[20px] md:leading-[1.4]',
                    'rounded-t-none rounded-br-none rounded-bl-2xl',
                    'border-0 outline-none appearance-none',
                    'transition-[background,box-shadow] duration-200',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#26A9E0] focus-visible:ring-offset-1 md:focus-visible:ring-offset-2',
                    mainHomeTab === 'score'
                      ? 'shadow-[0_4px_12px_rgba(13,145,200,0.35)] z-[1]'
                      : 'shadow-[0_4px_12px_rgba(0,61,87,0.22)] hover:brightness-110'
                  )}
                  style={
                    mainHomeTab === 'score'
                      ? { background: 'linear-gradient(180deg, #1EB8F8 0%, #0D91C8 100%)' }
                      : { background: 'linear-gradient(180deg, #118FC3 0%, #003D57 100%)' }
                  }
                >
                  <img
                    src={wcTabScoreIcon}
                    alt=""
                    className="h-5 w-5 shrink-0 object-contain md:h-8 md:w-8"
                    draggable={false}
                  />
                  <span className="whitespace-nowrap">Dự đoán tỉ số</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMainHomeTab('worldcup')}
                  className={twMerge(
                    'box-border min-w-0 flex-1 md:w-[300px] md:max-w-[min(300px,100vw)] md:flex-none md:shrink-0',
                    'flex h-[44px] items-center justify-center gap-1.5 px-2 py-1.5 text-center md:h-[52px] md:gap-2.5 md:px-10 md:py-2',
                    'font-roboto font-black uppercase text-[13px] leading-[1.2] text-white md:text-[20px] md:leading-[1.4]',
                    'rounded-t-none rounded-bl-none rounded-br-2xl',
                    'border-0 outline-none appearance-none',
                    'transition-[background,box-shadow] duration-200',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#26A9E0] focus-visible:ring-offset-1 md:focus-visible:ring-offset-2',
                    mainHomeTab === 'worldcup'
                      ? 'shadow-[0_4px_12px_rgba(13,145,200,0.35)] z-[1]'
                      : 'shadow-[0_4px_12px_rgba(0,61,87,0.22)] hover:brightness-110'
                  )}
                  style={
                    mainHomeTab === 'worldcup'
                      ? { background: 'linear-gradient(180deg, #1EB8F8 0%, #0D91C8 100%)' }
                      : { background: 'linear-gradient(180deg, #118FC3 0%, #003D57 100%)' }
                  }
                >
                  <img
                    src={wcTabResultIcon}
                    alt=""
                    className="h-5 w-5 shrink-0 object-contain md:h-8 md:w-8"
                    draggable={false}
                  />
                  <span className="whitespace-nowrap">Dự đoán kết quả</span>
                </button>
              </div>
            </div>

            <div className="p-2 sm:p-3 md:p-4">
              {mainHomeTab === 'score' ? (
                <>
                  <div className="flex space-x-6 mt-0 justify-center items-center">
                    <Button
                      type="link"
                      icon={<LeftOutlined />}
                      onClick={scrollLeft}
                      className="text-[#26A9E0] !px-0 !mx-0"
                    />
                    <div
                      className="flex overflow-x-auto scrollbar-custom pt-1 pb-2 !mx-0"
                      ref={leagueListRef}
                      style={{ scrollBehavior: 'smooth' }}
                    >
                      {leagues.map((league) => (
                        <div
                          key={league.id}
                          className={twMerge(
                            'flex justify-center items-center p-1 mx-2 rounded-lg cursor-pointer transition-all duration-300 hover:scale-105',
                            selectedLeagues.includes(league.id)
                              ? 'bg-black/15 shadow-lg shadow-[#26A9E0] border-2 border-[#26A9E0]'
                              : 'bg-white/80 backdrop-blur-sm border-2 border-[#26A9E0]/30 hover:border-[#26A9E0] hover:shadow-md'
                          )}
                          style={{ width: '80px', minWidth: '80px', border: '1px solid #26A9E0' }}
                          onClick={() => handleLeagueClick(league.id)}
                        >
                          <img
                            src={league.logo}
                            alt={league.name}
                            className="h-12 object-contain"
                          />
                        </div>
                      ))}
                    </div>
                    <Button
                      type="link"
                      icon={<RightOutlined />}
                      onClick={scrollRight}
                      className="text-[#26A9E0] !px-0 !mx-0"
                    />
                  </div>
                  <div className="w-full h-auto my-2 md:my-2">
                    <div className="flex flex-wrap gap-1 sm:gap-3 lg:gap-5 items-center justify-center w-full">
                      <button
                        onClick={() => setActiveTab('all')}
                        className={`h-[26px] sm:h-[36px] lg:h-[40px] px-2 sm:px-4 lg:px-6 py-1 rounded-[12px] sm:rounded-[18px] lg:rounded-[25px] text-[11px] lg:text-[15px] font-normal ${
                          activeTab === 'all' ? 'text-white' : 'text-[#3C3C3C]'
                        } transition-all duration-200 outline-none focus:outline-none capitalize border-solid border-2 border-[#26A9E0] ${
                          activeTab === 'all' ? 'bg-[#26A9E0]' : 'bg-[#FFF]'
                        }`}
                      >
                        Tất Cả
                      </button>
                      <button
                        onClick={() => setActiveTab('today')}
                        className={`h-[26px] sm:h-[36px] lg:h-[40px] px-2 sm:px-4 lg:px-6 py-1 rounded-[12px] sm:rounded-[18px] lg:rounded-[25px] text-[11px] lg:text-[15px] font-normal ${
                          activeTab === 'today' ? 'text-white' : 'text-[#3C3C3C]'
                        } transition-all duration-200 border-solid border-2 border-[#26A9E0] outline-none focus:outline-none capitalize ${
                          activeTab === 'today' ? 'bg-[#26A9E0]' : 'bg-[#FFF]'
                        }`}
                      >
                        Hôm Nay
                      </button>
                      <button
                        onClick={() => setActiveTab('tomorrow')}
                        className={`h-[26px] sm:h-[36px] lg:h-[40px] px-2 sm:px-4 lg:px-6 py-1 rounded-[12px] sm:rounded-[18px] lg:rounded-[25px] text-[11px] lg:text-[15px] font-normal ${
                          activeTab === 'tomorrow' ? 'text-white' : 'text-[#3C3C3C]'
                        } transition-all duration-200 border-solid border-2 border-[#26A9E0] outline-none focus:outline-none capitalize ${
                          activeTab === 'tomorrow' ? 'bg-[#26A9E0]' : 'bg-[#FFF]'
                        }`}
                      >
                        Ngày Mai
                      </button>
                      {/* <button
                onClick={() => setShowSchedulePopup(true)}
                className="h-[26px] sm:h-[36px] lg:h-[40px] px-2 sm:px-4 lg:px-6 py-1 rounded-[12px] sm:rounded-[18px] lg:rounded-[25px] text-[11px] lg:text-[15px] font-normal text-[#FFF] transition-all duration-200 outline-none focus:outline-none capitalize bg-gradient-to-b bg-[#26A9E0] hover:opacity-90 border-solid border-2 border-[#26A9E0]"
              >
                Lịch Thi Đấu
              </button> */}
                    </div>
                  </div>
                  <div className="w-full h-full">
                    <Form
                      name="formMatch"
                      onFinish={onFinishForm}
                      onFinishFailed={onFinishFailed}
                      autoComplete="off"
                      form={form}
                      initialValues={{ account: '' }}
                    >
                      <div className="w-full h-full">
                        {isLoading ? (
                          <div className="w-full h-full text-center py-8">
                            <Spin />
                          </div>
                        ) : (
                          <div className="flex flex-col w-full h-full items-center mt-2">
                            <div className="flex w-full h-full items-center justify-center flex-col gap-2 md:mx-2 md:flex-row">
                              <div className="hidden flex gap-2 md:py-4">
                                <Form.Item
                                  name="account"
                                  className="mb-0"
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Vui lòng điền tên đăng nhập!'
                                    }
                                  ]}
                                >
                                  <Input
                                    placeholder="Nhập tên đăng nhập ..."
                                    disabled={true}
                                    maxLength={20}
                                    prefix={<UserOutlined />}
                                    className="w-[200px] md:w-[250px] h-[40px] md:h-[44px] text-base" // Thêm classes này
                                    style={{
                                      fontSize: '16px'
                                    }}
                                  />
                                </Form.Item>
                                <Form.Item className="mb-0">
                                  <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={isLoadingSubmit}
                                    className="text-white font-medium rounded-[4.286px] border-0 h-[36px] md:h-[40px]" // Điều chỉnh chiều cao button
                                    style={{
                                      background: '#26A9E0'
                                    }}
                                  >
                                    Xác Nhận
                                  </Button>
                                </Form.Item>
                              </div>
                            </div>
                            <div className="w-full h-full flex flex-col gap-2 mt-2">
                              {showListItemMatch(getFilteredMatches())}
                            </div>
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-2"></div>
                      </div>
                    </Form>
                  </div>
                </>
              ) : (
                renderWorldCupSection()
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Popup Lịch Thi Đấu */}
      {showSchedulePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4">
              <h2 className="text-xl font-bold text-[#  ]">LỊCH THI ĐẤU HOT</h2>
              <button
                onClick={() => setShowSchedulePopup(false)}
                className="w-8 h-8 bg-[#26A9E0] text-white rounded-full flex items-center justify-center hover:bg-[#26A9E0] transition-colors"
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              <iframe
                src="https://football-widget.pages.dev/"
                width="100%"
                height="600"
                frameBorder="0"
                allowFullScreen
                title="Football Widget"
                className="w-full rounded-lg"
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        open={voteConfirm.open}
        onCancel={closeVoteConfirm}
        centered
        width={360}
        wrapClassName="vote-confirm-modal-wrap"
        title={
          <span className="text-sm font-semibold leading-snug text-[#111827]">
            Xác nhận dự đoán: {voteConfirm.title}
          </span>
        }
        styles={{
          header: { padding: '10px 14px', marginBottom: 0 },
          body: { padding: '10px 14px 14px' },
          footer: { padding: '8px 14px 12px', marginTop: 0 }
        }}
        footer={[
          <Button key="cancel" size="small" onClick={closeVoteConfirm} disabled={isVoteSubmitting}>
            Hủy
          </Button>,
          <Button
            key="ok"
            type="primary"
            size="small"
            onClick={handleConfirmVote}
            loading={isVoteSubmitting}
            style={{ background: '#26A9E0' }}
          >
            Xác nhận
          </Button>
        ]}
      >
        <div className="w-fit max-w-full rounded-lg border border-[#d9dde5] bg-[#f3f4f6] px-2.5 py-1.5">
          <div className="text-[10px] font-bold uppercase tracking-wide text-[#718096]">
            Bạn đã chọn
          </div>
          <div className="mt-0.5 text-lg font-bold leading-tight text-[#0f172a]">
            {voteConfirm.selectedName}
          </div>
        </div>

        {voteConfirm.error ? (
          <div className="mt-2 flex items-start gap-2 rounded-lg border border-[#f1b2b2] bg-[#fff1f0] px-2.5 py-1.5 text-xs text-[#5f2120]">
            <span className="mt-0.5 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-[#e5484d] text-[9px] font-bold text-white">
              ×
            </span>
            <span>{voteConfirm.error}</span>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default HI88Page;
